import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { Form } from 'antd';
import dayjs from 'lib/dayjs';
import { FieldArray, useFormikContext } from 'formik';
import styled from '@emotion/styled';
import useSmartField from 'hooks/useSmartField';
import FormField from 'components/FormField';
import { useSize } from 'ahooks';
import { PlusCircleFilled, CloseCircleFilled } from '@ant-design/icons';
import { useTranslate } from '@tolgee/react';
import { DatePicker } from 'components/DatePicker';

type TimeGroupProps = {
  fieldNamePrefix: string;
};

const iconStyle = {
  color: '#84868C',
  cursor: 'pointer',
};

function newItem() {
  let startAt = undefined;
  let endAt = undefined;
  return {
    startAt,
    endAt,
  };
}

function YearGroup(props: TimeGroupProps) {
  const { fieldNamePrefix } = props;
  const { validateForm } = useFormikContext();
  const { field: yearRepeatField, helpers: yearRepeatHelpers } = useSmartField(fieldNamePrefix);
  useEffect(() => {
    if (!yearRepeatField?.value || yearRepeatField?.value?.length === 0) {
      yearRepeatHelpers.setValue([newItem()]);
    }
  }, [yearRepeatField, yearRepeatHelpers]);
  const { t } = useTranslate();

  if (!yearRepeatField?.value || yearRepeatField?.value?.length === 0) return null;

  return (
    <Form.Item label={t('multiTimeSelect.时间')} required>
      <Container>
        <FieldArray name={fieldNamePrefix}>
          {(arrayHelpers) => {
            return yearRepeatField?.value?.map((each, index) => {
              return (
                <TimeItem key={index}>
                  <FormField
                    name={`${fieldNamePrefix}.${index}`}
                    validateTrigger={['onSubmit', 'onBlur']}
                    validate={(value) => {
                      if (!value || !value?.startAt || !value?.endAt) {
                        return t('multiTimeSelect.请填写时间');
                      }
                      const times = yearRepeatField?.value;
                      const before = times?.[index - 1];
                      const after = times?.[index + 1];
                      if (before) {
                        if (dayjs(value.startAt, 'HH:mm').isBefore(dayjs(before.endAt, 'HH:mm'))) {
                          return t('multiTimeSelect.earlierTip');
                        }
                      }
                      if (after) {
                        if (dayjs(value.endAt, 'HH:mm').isAfter(dayjs(after.startAt, 'HH:mm'))) {
                          return t('multiTimeSelect.earlierTip');
                        }
                      }
                    }}
                  >
                    {({ field }) => {
                      const { value, onChange } = field;
                      return (
                        <DatePicker.RangePicker
                          showTime={{ format: 'HH:mm' }}
                          value={
                            each.startAt
                              ? [
                                  dayjs(each.startAt, 'MM-DD HH:mm'),
                                  dayjs(each.endAt, 'MM-DD  HH:mm'),
                                ]
                              : undefined
                          }
                          format="MM-DD HH:mm"
                          mode={['date', 'date']}
                          onChange={(values) => {
                            if (!value) {
                              onChange({ startAt: undefined, endAt: undefined });
                            } else {
                              onChange({
                                startAt: values?.[0]?.format('MM-DD HH:mm'),
                                endAt: values?.[1]?.format('MM-DD HH:mm'),
                              });
                            }
                          }}
                          onOpenChange={(status) => {
                            if (!status) {
                              field.onBlur();
                              validateForm();
                            } else {
                              const yearBtn = document.querySelector('.ant-picker-year-btn');
                              yearBtn.style.display = 'none';
                              const superPrevBtn = document.querySelector(
                                '.ant-picker-header-super-prev-btn',
                              );
                              superPrevBtn.style.display = 'none';
                              const superNextBtn = document.querySelector(
                                '.ant-picker-header-super-next-btn',
                              );
                              superNextBtn.style.display = 'none';
                            }
                          }}
                        />
                      );
                    }}
                  </FormField>
                  {index === 0 ? (
                    <PlusCircleFilled
                      title={
                        yearRepeatField?.value?.length >= 4
                          ? t('multiTimeSelect.最多可增加4个时间段')
                          : t('multiTimeSelect.新增时间')
                      }
                      onClick={() => {
                        if (yearRepeatField?.value?.length < 4) {
                          arrayHelpers.push(newItem());
                        }
                      }}
                      style={{
                        ...iconStyle,
                        color:
                          yearRepeatField?.value?.length >= 4 ? 'rgb(204, 207, 215)' : '#84868C',
                        cursor: yearRepeatField?.value?.length >= 4 ? 'not-allowed' : 'pointer',
                      }}
                    />
                  ) : (
                    <CloseCircleFilled
                      onClick={() => {
                        if (yearRepeatField?.value?.length > 1) {
                          arrayHelpers.remove(index);
                        }
                      }}
                      style={iconStyle}
                    />
                  )}
                </TimeItem>
              );
            });
          }}
        </FieldArray>
      </Container>
    </Form.Item>
  );
}

const Container = styled.div``;

const TimeItemImpl = styled.div<{ paddingRight: number; error: boolean }>`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 8px;
  & > span {
    display: flex;
    align-items: center;
    height: 32px;
  }
  .ant-picker-range {
    margin-right: 8px;
    padding-right: ${(props) => props.paddingRight - 280}px;
    .ant-picker-suffix {
      position: absolute;
      right: 11px;
    }
  }
  .ant-picker-focused {
    ${(props) => (props.error ? 'border-color: red;box-shadow: none;' : '')}
  }
`;

function TimeItem(props: { children: ReactNode }) {
  const { children, validateError } = props;
  const ref = useRef(null);
  const size = useSize(ref);
  const paddingRight = size?.width || 0;

  return (
    <TimeItemImpl error={!!validateError} ref={ref} paddingRight={paddingRight}>
      {children}
    </TimeItemImpl>
  );
}

export default YearGroup;
